import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import { useDebounce } from 'use-debounce'
import { useEffect, useState } from 'react'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { InputAdornment } from '@mui/material'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import Autocomplete, { AutocompleteRenderGroupParams } from '@mui/material/Autocomplete'

import { HeaderSearchOption } from '../../types/'
import { HeaderSearchAssetType } from '../../enums/enums'
import { useHeaderSearchResults } from '../../stores/search-store'

import HeaderSearchGroup from './HeaderSearchGroup'
import HeaderSearchFilter from './HeaderSearchFilter'

const PREFIX = 'HeaderSearch'

const classes = {
  root: `${PREFIX}-root`,
  open: `${PREFIX}-open`,
  close: `${PREFIX}-close`,
}

const Root = styled('div')(({ theme }) => ({
  '& .MuiAutocomplete-root .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
    fontSize: 14,
  },
  [`& .${classes.open}`]: {
    width: '250px',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflow: 'hidden',
    [theme.breakpoints.up('sm')]: {
      width: '330px',
    },
  },
  [`& .${classes.close}`]: {
    width: 0,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflow: 'hidden',
  },
}))

const StyledPaper = styled(Paper)(() => ({
  marginTop: '2px',
  border: '1px solid #bdbdbd',
  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 20%)',
  '& .MuiAutocomplete-noOptions': {
    padding: '10px',
    fontSize: 14,
    color: '#404041',
  },
  '& .MuiAutocomplete-listbox': {
    padding: '0 0 8px 0',
  },
}))

function HeaderSearch(): JSX.Element {
  const { t } = useTranslation()
  const [openSearchBar, setOpenSearchBar] = useState<boolean>(false)
  const [options, setOptions] = useState<HeaderSearchOption[]>([])
  const [inputValue, setInputValue] = useState('')
  const [debouncedInputValue] = useDebounce(inputValue, 1000)
  const [filter, setFilter] = useState<HeaderSearchAssetType | null>(HeaderSearchAssetType.all)
  const { data: searchResult, isLoading: searchResultLoading = false } = useHeaderSearchResults(
    debouncedInputValue,
    filter === HeaderSearchAssetType.all ? null : filter
  )

  const handleFilter = (event: React.MouseEvent<HTMLElement>, newValue: HeaderSearchAssetType | null) => {
    setFilter(newValue)
  }

  useEffect(() => {
    setOptions(searchResult ?? [])

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, debouncedInputValue, searchResultLoading])

  return (
    <Root>
      <Grid container>
        <Grid item>
          <IconButton
            color="inherit"
            aria-label="upload picture"
            component="span"
            onClick={() => {
              setOpenSearchBar(true)
            }}
            sx={{ display: openSearchBar ? 'none' : 'flex' }}
          >
            <SearchOutlinedIcon />
          </IconButton>
        </Grid>

        <Grid item className={openSearchBar ? classes.open : classes.close}>
          {openSearchBar ? (
            <Autocomplete
              onBlur={() => {
                setOpenSearchBar(false)
              }}
              options={options}
              groupBy={(option) => option.group}
              getOptionLabel={(option: HeaderSearchOption) => option.FullName ?? option.Name ?? ''}
              loading={searchResultLoading}
              onChange={(event: any, newValue: HeaderSearchOption | null) => {
                setOptions(newValue ? [newValue, ...options] : options)
              }}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue)
              }}
              renderInput={(params) => {
                params.InputProps.startAdornment = openSearchBar && (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon />
                  </InputAdornment>
                )
                params.InputProps.endAdornment = null
                return <TextField {...params} placeholder={t('search')} size="small" autoFocus />
              }}
              renderOption={(props, option) => {
                return (
                  <ul key={option.id}>
                    <li {...props} key={option.FullName ?? option.Name ?? option.id}>
                      {option.FullName ?? option.Name ?? ''}
                    </li>
                    <li key={option.Email}>option.Email</li>
                  </ul>
                )
              }}
              renderGroup={(params: AutocompleteRenderGroupParams) => {
                return (
                  <li key={params.key} style={{ padding: 0 }}>
                    <HeaderSearchGroup data={params} />
                  </li>
                )
              }}
              PaperComponent={({ children }) => (
                <StyledPaper>
                  <HeaderSearchFilter value={filter} onChange={handleFilter} />
                  <Grid sx={{ p: 0 }}>
                    {inputValue.length > 1 ? (
                      children
                    ) : (
                      <Typography sx={{ p: '10px', fontSize: 14, color: '#404041' }}>{t('continueTyping')}</Typography>
                    )}
                  </Grid>
                </StyledPaper>
              )}
            />
          ) : null}
        </Grid>
      </Grid>
    </Root>
  )
}

export default HeaderSearch
